import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => (v || v === 0 ? Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "—");
const Row = ({ k, v }) => (
  <div className="flex justify-between gap-6 py-2 border-b border-[#1C1C16] text-[13px]">
    <span className="text-[#8A8A8A]">{k}</span>
    <span className="text-[#ECECE4] text-right font-medium">{v || "—"}</span>
  </div>
);

export default function VerifyNoteSale() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, cert: null, error: "" });
  const [qr, setQr] = useState("");

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(window.location.href, { margin: 1, width: 200, errorCorrectionLevel: "H" }).then((u) => alive && setQr(u)).catch(() => {});
    axios.get(`${API}/note-sale/verify/${token}`)
      .then((r) => alive && setState({ loading: false, cert: r.data.cert, error: "" }))
      .catch((e) => alive && setState({ loading: false, cert: null, error: e?.response?.status === 404 ? "No Bank Note matches this verification code." : "Verification failed." }));
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] flex flex-col items-center px-4 py-16" data-testid="verify-note-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="font-serif text-[22px] tracking-tight leading-none">SEUTRADE FINANCE</div>
          <div className="font-serif text-[10px] tracking-[0.22em] uppercase text-[#B8860B] mt-1">Financial Services</div>
        </div>

        {state.loading ? (
          <div className="flex items-center justify-center gap-2 text-[#8A8A8A] py-16" data-testid="verify-loading">
            <Loader2 size={16} className="animate-spin" /> Verifying…
          </div>
        ) : state.error ? (
          <div className="rounded-xl border border-[#5a2020] bg-[#1a0e0e] p-8 text-center" data-testid="verify-error">
            <ShieldAlert size={34} className="text-[#FF8585] mx-auto mb-3" />
            <div className="text-[15px] font-semibold text-[#FFB4B4]">Not verified</div>
            <p className="text-[13px] text-[#c99] mt-2">{state.error}</p>
          </div>
        ) : (
          <div className="rounded-xl border border-[#2A2A22] bg-[#0F0F0A] p-7" data-testid="verify-certificate">
            <div className="flex items-center gap-2 justify-center mb-5">
              <ShieldCheck size={22} className="text-[#3DBB8E]" />
              <span className="text-[15px] font-semibold text-[#7EE0BC]">Authentic — Verified Bank Note</span>
            </div>
            <div className="text-center mb-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A8A]">Bank Note Serial</div>
              <div className="font-mono text-[20px] text-[#E4C97A] mt-1" data-testid="verify-serial">{state.cert.serial}</div>
            </div>
            <Row k="Seller / Holder" v={state.cert.sellerName} />
            <Row k="Purchaser" v={state.cert.buyerName} />
            <Row k="Maker of Subject Note" v={state.cert.makerName} />
            <Row k="Accepting Bank" v={state.cert.acceptanceBankName} />
            <Row k="Face Value" v={money(state.cert.faceValue)} />
            <Row k="Purchase Price" v={money(state.cert.purchasePrice)} />
            <Row k="Draft No." v={state.cert.draftNo} />
            <Row k="Registration / EIN" v={state.cert.regNo} />
            {state.cert.intlRegNo ? <Row k="Intl. Reg. No." v={state.cert.intlRegNo} /> : null}
            <Row k="Maturity" v={state.cert.maturityDate} />
            <Row k="Minted" v={state.cert.minted_at ? new Date(state.cert.minted_at).toLocaleString() : "—"} />
            {qr ? (
              <div className="flex flex-col items-center mt-6">
                <img src={qr} alt="Verify QR" className="w-28 h-28 rounded bg-white p-1" />
                <div className="text-[10px] text-[#6d6d64] mt-2">Scan to re-verify this record</div>
              </div>
            ) : null}
          </div>
        )}
        <p className="text-center text-[10.5px] text-[#6d6d64] mt-6 leading-relaxed">
          This record confirms the issuance details of a Private Negotiable Instrument. It is a private commercial
          instrument and is not FDIC-insured, legal tender, or a government obligation.
        </p>
      </div>
    </div>
  );
}
